import { DndContext, DragOverlay, MouseSensor, pointerWithin, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DraggableWord } from '../../components/DraggableWord';
import { Word } from '../../components/Word';
import { WordBank } from '../../components/WordBank';
import { TextController } from '../../components/TextController';
import { createPortal } from 'react-dom';
import { COMPLETAR_SOUNDS } from '../../utils/constants';
import { useGameSounds } from '@educaplay/core';
import { useSelector } from 'react-redux';
import { normalizeGameState } from '../../utils/gameState';

const getOrigin = (element) => {
    if (!element) return null
    //if element is button, return its origin
    if (element.tagName === 'BUTTON') {
        return element.dataset.origin
    }
    return element.closest('button').dataset.origin
}

export const DndGame = (props) => {
    const { gameState, setGameState, animated, onCompleted, ref } = props;
    const [activeId, setActiveId] = useState(gameState.idBlanks?.[0].id);
    const [draggingId, setDraggingId] = useState(null);

    const isAnimating = useSelector(state => state.game.isAnimating);

    const wordBankRef = useRef(null);
    const textControllerRef = useRef(null);
    
    const gameSounds = useGameSounds();

    useImperativeHandle(ref, () => ({
        entryAnimation: async () => {
            gameSounds.play(COMPLETAR_SOUNDS.LOAD_BANK);
            wordBankRef.current.entryAnimation();
            textControllerRef.current.entryAnimation({ delay: .3 });
        },
        checkAnimation: async (userAnswer) => {
            if (!userAnswer) return;
            const wordIds = Object.keys(userAnswer);
            
            for (let i = 0; i < wordIds.length; i++) {
                const id = wordIds[i];
                const status = userAnswer[id];

                const word = textControllerRef.current?.getWordRef(id); 
                if(!word) continue;

                if (status === 'correct') {
                    await word.success();
                } else if (status === 'wrong') {
                    await word.fail();
                } else {
                    await word.empty();
                }
            }
        }
    }));

    useEffect(() => {
        if(activeId === null) {
            onCompleted?.();
        }
    }, [activeId, onCompleted])

    const sensors = useSensors(
        useSensor(MouseSensor, {
            // Require the mouse to move by 10 pixels before activating
            activationConstraint: {
                distance: 15,
            },
        }),
        useSensor(TouchSensor, {
            // Press delay of 250ms, with tolerance of 5px of movement
            activationConstraint: {
                delay: 100,
                tolerance: 10,
            },
        }),
        // useSensor(KeyboardSensor)
    );

    if (!gameState.idBlanks) return null

    const handleDragEnd = (evt) => {
        const active = evt.active
        const over = evt.over
        const origin = getOrigin(evt.activatorEvent.srcElement)
        if (!origin) return
        //If dropped anywhere but a dropzone, reset it in the wordBank
        if (!over) {
            const newGameState = {
                ...gameState,
                words: gameState.words.map(word => {
                    if (active.id === word.id) {
                        return {
                            ...word,
                            used: false
                        }
                    }
                    return word 
                }),
                idBlanks: gameState.idBlanks.map(blank => {
                    if (active.id === blank.fill) {
                        return {
                            ...blank,
                            fill: null
                        }
                    }
                    return blank
                })
            }
            setGameState(newGameState)
            if (/^text_/.test(origin)) {
                const originId = origin.split('_')[1]
                setActiveId(originId)
            }
            return
        }

        //If dropped in an already correct dropzone, reset word and do nothing
        if(gameState.idBlanks.find(blank => blank.id === over.id).isCorrect) {
            if (!gameState.idBlanks.find(blank => blank.fill === active.id)) {
                setGameState({
                    ...gameState,
                    words: gameState.words.map(word => {
                        if (active.id === word.id) {
                            return {
                                ...word,
                                used: false
                            }
                        }
                        return word 
                    })
                })
            }
            return
        }

        //If dropped in a dropzone with a word, swap them
        if (/^text_/.test(origin) && gameState.idBlanks.find(blank => blank.id === over.id).fill) {
            const swappedWordId = gameState.idBlanks.find(blank => blank.id === over.id).fill
            const originBlank = origin.split('_')[1]
            const newGameState = {
                ...gameState,
                idBlanks: gameState.idBlanks.map(blank => {
                    if (blank.id === originBlank) {
                        return {
                            ...blank,
                            fill: swappedWordId,
                            isCorrect: null
                        }
                    }
                    if (over.id === blank.id) {
                        return {
                            ...blank,
                            fill: active.id,
                            isCorrect: null
                        }
                    }
                    return blank
                }),
                words: gameState.words.map(word => {
                    if ([active.id, swappedWordId].includes(word.id)) {
                        return {
                            ...word,
                            isCorrect: null
                        }
                    }
                    return word
                })
            }
            setGameState(newGameState)
            return
        }
        //If dropped in a dropzone, fill it
        const newGameState = {
            ...gameState,
            words: gameState.words.map(word => {
                if (active.id === word.id) {
                    return {
                        ...word,
                        used: true
                    }
                }
                //if there was a word in the dropzone, reset it in the wordBank
                const activeBlank = gameState.idBlanks.find(blank => blank.id === over?.id) ?? null
                if (activeBlank !== null && activeBlank.fill === word.id) {
                    return {
                        ...word,
                        used: false,
                        isCorrect: null
                    }
                }
                return word 
            }),
            idBlanks: gameState.idBlanks.map(blank => {
                if (over.id === blank.id) {
                    return {
                        ...blank,
                        fill: active.id,
                        isCorrect: null
                    }
                }
                return blank
            })
        }
        setGameState(newGameState)
        const wordRef = textControllerRef.current?.getWordRef(over.id);
        if(wordRef) wordRef.focus({hasDelay: true});
        setActiveId(over.id);
        nextActiveId(newGameState.idBlanks);
    }

    const handleDragStart = (evt) => {
        if (isAnimating) return
        gameSounds.play(COMPLETAR_SOUNDS.MOVE)
        setDraggingId(evt.active.id)
        const gameStateNormalized = normalizeGameState(gameState)
        const newGameState = {
            ...gameStateNormalized,
            words: gameStateNormalized.words.map(word => {
                if (evt.active.id === word.id) {
                    return {
                        ...word,
                        used: true,
                        isCorrect: null
                    }
                }
                return word
            }),
            idBlanks: gameStateNormalized.idBlanks.map(blank => {
                if (evt.active.id === blank.fill) {
                    return {
                        ...blank,
                        fill: null,
                        isCorrect: null
                    }
                }
                return blank
            })
        }
        setGameState(newGameState)
    }

    const nextActiveId = (blanks) => {
        setActiveId((currentActiveId) => {
            // Encontrar el índice actual del elemento activo
            const currentIndex = blanks.findIndex(blank => blank.id === currentActiveId);
        
            // Crear un array reordenado desde la posición actual
            const reorderedBlanks = [
                ...blanks.slice(currentIndex + 1),
                ...blanks.slice(0, currentIndex + 1)
            ];
        
            // Buscar el primer elemento con fill === null
            const nextBlank = reorderedBlanks.find(blank => blank.fill === null);
        
            // Retornar el id del siguiente elemento o null si no hay ninguno
            return nextBlank ? nextBlank.id : null;
        });
    }

    const clickWordHandler = (id) => {
        if (isAnimating) return
        if(activeId === null) return
        gameSounds.play(COMPLETAR_SOUNDS.CLICK)
        setTimeout(() => gameSounds.play(COMPLETAR_SOUNDS.MOVE), 200)
        const gameStateNormalized = normalizeGameState(gameState)
        const newGameState = {
            ...gameStateNormalized,
            words: gameStateNormalized.words.map(word => {
                if (id === word.id) {
                    return {
                        ...word,
                        used: true
                    }
                }
                return word
            }),
            idBlanks: gameStateNormalized.idBlanks.map(blank => {
                if (blank.id === activeId) {
                    return {
                        ...blank,
                        fill: id,
                        isCorrect: null
                    }
                }
                return blank
            })
        }
        setGameState(newGameState);
        const wordRef = textControllerRef.current.getWordRef(activeId);
        if(wordRef) wordRef.focus({hasDelay: false});
        nextActiveId(newGameState.idBlanks);
    }

    const clickBlankHandler = (id) => {
        if (isAnimating) return
        if (gameState.idBlanks.find(blank => blank.id === id).isCorrect) return
        gameSounds.play(COMPLETAR_SOUNDS.ACTIVE)
        setActiveId(id);
    }

    return (
        <DndContext 
            collisionDetection={(pointerWithin)}
            sensors={sensors}
            onDragStart={handleDragStart} 
            onDragEnd={handleDragEnd}
            onDragCancel={() => setGameState(normalizeGameState(gameState))}
        >
            {/* <div className={classes.gameContainer}>
                <motion.div className={clsx(classes.gameWrapper, customClasses.mainContainer)} > */}
                    <WordBank ref={wordBankRef} isBottomBank animated={animated} >
                        {gameState.words.map(word => {
                            if (word.used) {
                                return (
                                    <Word
                                        word={word}
                                        key={word.id}
                                        place={'wordBank'}
                                        mode={'dnd'}
                                        styles={{
                                            ghost: word.used,
                                            correct: word.isCorrect
                                        }}
                                    />
                                )
                            }
                            return (
                                <DraggableWord key={word.id} word={word} onClick={clickWordHandler} origin={'wordBank'} />
                            )
                        })}
                    </WordBank>

                    <TextController
                        ref={textControllerRef}
                        gameState={gameState}
                        setGameState={setGameState}
                        clickBlankHandler={clickBlankHandler}
                        clickWordHandler={clickWordHandler}
                        activeId={activeId}
                        setActiveId={setActiveId}
                        animated={animated}
                    />

                    {createPortal(
                        <DragOverlay>
                            <DraggableWord word={gameState.words.find(word => word.id === draggingId)} styles={{dragging: true}} isDragging />
                        </DragOverlay>,
                        document.body
                    )}
                {/* </motion.div>
            </div> */}
        </DndContext>
    )
}